import { DashboardCard } from "@dashboard/components/Card";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TableButtonWrapper from "@dashboard/components/TableButtonWrapper/TableButtonWrapper";
import TableRowLink from "@dashboard/components/TableRowLink";
import { WsmErrorFragment } from "@dashboard/graphql";
import { getWsmIndexedErrors,getWsmKitsErrorMessage } from "@dashboard/wsmKits/errors";
import { WsmKitMemberDraft } from "@dashboard/wsmKits/types";
import { TableBody, TableCell, TableHead } from "@material-ui/core";
import { Box, Button, Input, Text, TrashBinIcon } from "@saleor/macaw-ui-next";
import React, { useMemo } from "react";
import { useIntl } from "react-intl";

import { messages } from "./messages";

const numberOfColumns = 4;

/** The server's LABEL_MAX_LENGTH (wsm-saleor wsm/kits/models.py). */
const LABEL_MAX_LENGTH = 250;

interface WsmKitMembersCardProps {
  members: WsmKitMemberDraft[];
  errors: WsmErrorFragment[];
  disabled: boolean;
  onAdd: () => void;
  onRemove: (key: string) => void;
  onQuantityChange: (key: string, quantity: number) => void;
  onLabelChange: (key: string, label: string) => void;
}

// ponytail: a plain table, not the canvas Datagrid. Four columns and two
// editable fields do not need a virtualiser, and the virtualiser is what put the
// row menu outside the table's own right border and dropped Quantity at 1024.
// ResponsiveTable already scrolls horizontally, so the affordance is free.
// Ceiling: a kit with hundreds of members would want virtualising again.
// Upgrade path: the stock Datagrid, once it can pin its action cell.
export const WsmKitMembersCard = ({
  members,
  errors,
  disabled,
  onAdd,
  onRemove,
  onQuantityChange,
  onLabelChange,
}: WsmKitMembersCardProps) => {
  const intl = useIntl();
  const rowErrors = useMemo(() => getWsmIndexedErrors(errors, "members"), [errors]);

  return (
    <DashboardCard data-test-id="wsm-kit-members">
      <DashboardCard.Header>
        <DashboardCard.Title>{intl.formatMessage(messages.title)}</DashboardCard.Title>
        <DashboardCard.Toolbar>
          <Button
            variant="secondary"
            disabled={disabled}
            onClick={onAdd}
            data-test-id="wsm-kit-add-member"
          >
            {intl.formatMessage(messages.addMember)}
          </Button>
        </DashboardCard.Toolbar>
      </DashboardCard.Header>
      <DashboardCard.Content paddingX={0}>
        <ResponsiveTable>
          <colgroup>
            <col />
            <col style={{ width: "36%" }} />
            <col style={{ width: "110px" }} />
            <col style={{ width: "64px" }} />
          </colgroup>
          <TableHead>
            <TableRowLink>
              <TableCell>{intl.formatMessage(messages.product)}</TableCell>
              <TableCell>{intl.formatMessage(messages.label)}</TableCell>
              <TableCell>{intl.formatMessage(messages.quantity)}</TableCell>
              <TableCell />
            </TableRowLink>
          </TableHead>
          <TableBody>
            {members.length === 0 && (
              <TableRowLink data-test-id="wsm-kit-members-empty">
                <TableCell colSpan={numberOfColumns}>
                  <Text size={3} color="default2">
                    {intl.formatMessage(messages.noMembers)}
                  </Text>
                </TableCell>
              </TableRowLink>
            )}
            {members.map((member, index) => {
              const error = rowErrors[index];

              return (
                <TableRowLink key={member.key} data-test-id="wsm-kit-member-row">
                  <TableCell data-test-id="wsm-kit-member-product">
                    {/*
                      The name wraps and the SKU rides under it, the order lines' shape. The
                      theme forces every MUI table to auto layout, so a nowrap name or a SKU
                      column sets the minimum widths and squeezes Label to a sliver.
                    */}
                    <Text size={3} display="block" wordBreak="break-word">
                      {member.productName}
                    </Text>
                    <Text size={2} color="default2" display="block" wordBreak="break-word">
                      {member.sku || member.variantName}
                    </Text>
                    {error && (
                      <Text size={2} color="critical1" data-test-id="wsm-kit-member-error">
                        {getWsmKitsErrorMessage(error, intl)}
                      </Text>
                    )}
                  </TableCell>
                  <TableCell>
                    {/* Empty shows the storefront's fallback, the product name. */}
                    <Input
                      size="small"
                      width="100%"
                      disabled={disabled}
                      value={member.label}
                      placeholder={member.productName}
                      maxLength={LABEL_MAX_LENGTH}
                      aria-label={intl.formatMessage(messages.label)}
                      data-test-id={`wsm-kit-member-label-${index}`}
                      onChange={event => onLabelChange(member.key, event.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      size="small"
                      type="number"
                      disabled={disabled}
                      value={String(member.quantity)}
                      error={!!error}
                      data-test-id={`wsm-kit-member-quantity-${index}`}
                      onChange={event =>
                        onQuantityChange(member.key, Number(event.target.value) || 0)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" justifyContent="flex-end">
                      <TableButtonWrapper>
                        <Button
                          variant="secondary"
                          icon={<TrashBinIcon />}
                          disabled={disabled}
                          onClick={() => onRemove(member.key)}
                          aria-label={intl.formatMessage(messages.removeMember)}
                          data-test-id={`wsm-kit-member-remove-${index}`}
                        />
                      </TableButtonWrapper>
                    </Box>
                  </TableCell>
                </TableRowLink>
              );
            })}
          </TableBody>
        </ResponsiveTable>
      </DashboardCard.Content>
    </DashboardCard>
  );
};

export default WsmKitMembersCard;
